import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  DcOverlaySlider,
  PixelFactoryContext,
  PixelTable,
  SectionContent,
} from 'pixel-base'
import styled from 'styled-components'
import { getCOAGroups, getChartOfAccounts } from 'utils/api-routes-accounts'
import {
  AccountFilters,
  AccountGroup,
  TreeNode,
} from './components/AccountFilters'
import AddNewAccount from './components/AddNewAccount'
import FinancePageShell from '../../components/FinancePageShell'
import { financeColors, financeRadius } from '../../components/tokens'

interface PixelFactoryType {
  showPageLoader?: () => void
  hidePageLoader?: () => void
}

interface Account {
  id: number
  code: string
  name: string
  description?: string
  classification?: string
  account_type: string
  internal_group: string
  reconcile: boolean
  allow_posting?: boolean
  is_active?: boolean
  is_control_account?: boolean
  default_partner_required?: boolean
  default_cost_center_required?: boolean
  balance?: number
}

const classificationTone = (value?: string) => {
  const normalized = (value || '').toLowerCase()

  if (normalized.includes('asset')) {
    return {
      accent: '#2563eb',
      accentSoft: 'rgba(37, 99, 235, 0.12)',
      accentDeep: '#1d4ed8',
    }
  }

  if (normalized.includes('liabil')) {
    return {
      accent: '#d97706',
      accentSoft: 'rgba(217, 119, 6, 0.14)',
      accentDeep: '#b45309',
    }
  }

  if (normalized.includes('equity')) {
    return {
      accent: '#7c3aed',
      accentSoft: 'rgba(124, 58, 237, 0.14)',
      accentDeep: '#6d28d9',
    }
  }

  if (normalized.includes('income') || normalized.includes('revenue')) {
    return {
      accent: '#059669',
      accentSoft: 'rgba(5, 150, 105, 0.14)',
      accentDeep: '#047857',
    }
  }

  if (normalized.includes('expense') || normalized.includes('cost')) {
    return {
      accent: '#dc2626',
      accentSoft: 'rgba(220, 38, 38, 0.12)',
      accentDeep: '#b91c1c',
    }
  }

  return {
    accent: financeColors.infoText,
    accentSoft: financeColors.surfaceInfo,
    accentDeep: financeColors.infoText,
  }
}

const StatusBadge = styled.span<{
  $tone: 'neutral' | 'success' | 'danger' | 'warning'
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(props) => {
    switch (props.$tone) {
      case 'success':
        return 'rgba(5, 150, 105, 0.12)'
      case 'danger':
        return 'rgba(220, 38, 38, 0.12)'
      case 'warning':
        return 'rgba(217, 119, 6, 0.14)'
      default:
        return financeColors.surfaceMuted
    }
  }};
  color: ${(props) => {
    switch (props.$tone) {
      case 'success':
        return '#047857'
      case 'danger':
        return '#b91c1c'
      case 'warning':
        return '#b45309'
      default:
        return financeColors.textMuted
    }
  }};
`

const ClassificationBadge = styled.span<{
  $accent: string
  $accentSoft: string
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${(props) => props.$accentSoft};
  color: ${(props) => props.$accent};
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${(props) => props.$accent};
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
  }
`

const TypeText = styled.div`
  color: ${financeColors.textStrong};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DimensionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${financeColors.surfaceMuted};
  color: ${financeColors.textMuted};
  font-size: 10px;
  font-weight: 600;
  margin-right: 4px;
  margin-bottom: 4px;
`

const DimensionWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const FilterBanner = styled.div<{
  $accent: string
  $accentSoft: string
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: ${financeRadius.md};
  border: 1px solid ${(props) => props.$accent};
  background: ${(props) => props.$accentSoft};
`

const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const FilterDot = styled.span<{ $accent: string }>`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${(props) => props.$accent};
  flex: 0 0 auto;
`

const FilterText = styled.div`
  min-width: 0;
  color: ${financeColors.textStrong};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FilterHint = styled.div`
  color: ${financeColors.textMuted};
  font-size: 11px;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 220px;
  font-size: 14px;
  color: #64748b;
  gap: 8px;
`

const EmptyTitle = styled.strong`
  color: #0f172a;
  font-size: 15px;
`

const PageLayout: React.FC<{
  children: React.ReactNode
  isShowSpinner: boolean
  onAddNew: () => void
  showAddNew: boolean
  onCloseAddNew: () => void
  selectedNode: TreeNode | null
  onAddNewClose: (_response: any, _action: string) => void
  treeNodes: TreeNode[]
}> = ({
  children,
  isShowSpinner,
  onAddNew,
  showAddNew,
  onCloseAddNew,
  selectedNode,
  onAddNewClose,
  treeNodes,
}) => (
  <>
    <FinancePageShell
      title="Chart of Accounts"
      breadcrumbs={[
        { name: 'Finance', active: false },
        { name: 'Chart of Accounts', active: true },
      ]}
      isLoading={isShowSpinner}
      primaryAction={{ label: 'Add New', onClick: onAddNew }}
    >
      <div
        style={{
          overflow: 'auto',
          maxHeight: 'calc(100vh - 182px)',
        }}
      >
        {children}
      </div>
    </FinancePageShell>

    <DcOverlaySlider
      isOpen={showAddNew}
      onClose={() => onCloseAddNew()}
      title="Add New Account"
      width="34%"
      padding="0px"
    >
      <AddNewAccount
        selectedID={
          selectedNode
            ? { id: Number(selectedNode.id), title: selectedNode.name }
            : { id: null, title: 'Root' }
        }
        handleClose={onAddNewClose}
        treeNodes={treeNodes}
      />
    </DcOverlaySlider>
  </>
)

export { PageLayout }

const processGroupsToTreeNodes = (
  groups: Record<string, AccountGroup>,
): TreeNode[] => {
  const nodes: TreeNode[] = []

  const processGroup = (
    group: AccountGroup,
    parentId: string = '',
  ): TreeNode[] => {
    const groupNodes: TreeNode[] = []
    if (group.children) {
      Object.entries(group.children).forEach(([key, child]) => {
        const node: TreeNode = {
          id: key,
          name: child.label,
          type: key,
          code: key,
          level: parentId ? 1 : 0,
          children: processGroup(child, key),
        }
        groupNodes.push(node)
      })
    }
    return groupNodes
  }

  Object.entries(groups).forEach(([key, group]) => {
    if (key !== 'balance_sheet' && key !== 'profit_loss') {
      nodes.push(...processGroup(group))
    } else if (group.children) {
      Object.entries(group.children).forEach(([childKey, child]) => {
        const node: TreeNode = {
          id: childKey,
          name: child.label,
          type: childKey,
          code: childKey,
          level: 0,
          children: processGroup(child, childKey),
        }
        nodes.push(node)
      })
    }
  })

  return nodes
}

const collectNodeTypes = (node: TreeNode | null): Set<string> => {
  const values = new Set<string>()

  const walk = (current: TreeNode | null) => {
    if (!current) return
    values.add(current.type)
    values.add(current.code)
    current.children?.forEach((child) => walk(child))
  }

  walk(node)
  return values
}

const ChartOfAccounts: React.FC = () => {
  const pixelFactory = useContext(PixelFactoryContext) as PixelFactoryType
  const [accounts, setAccounts] = useState<Account[]>([])
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([])
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null)
  const [accountHeads, setAccountHeads] = useState<Account[]>([])
  const [isShowSpinner, setIsShowSpinner] = useState(false)
  const [showAddNew, setShowAddNew] = useState(false)

  useEffect(() => {
    pixelFactory?.showPageLoader?.()
    setIsShowSpinner(true)

    const subscription = getCOAGroups().subscribe({
      next: (res: any) => {
        if (res.response?.data) {
          const nodes = processGroupsToTreeNodes(res.response.data)
          setTreeNodes(nodes)
        }
      },
      error: (error) => {
        console.error('Error fetching account groups:', error)
        setIsShowSpinner(false)
        pixelFactory?.hidePageLoader?.()
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [pixelFactory])

  useEffect(() => {
    const subscription = getChartOfAccounts({
      view: 'all',
      include_inactive: true,
    }).subscribe({
      next: (res: any) => {
        if (res.response?.data) {
          setAccounts(res.response.data)
          setAccountHeads(res.response.data)
          setIsShowSpinner(false)
          pixelFactory?.hidePageLoader?.()
        }
      },
      error: (error) => {
        console.error('Error fetching accounts:', error)
        setIsShowSpinner(false)
        pixelFactory?.hidePageLoader?.()
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [pixelFactory])

  useEffect(() => {
    if (!selectedNode) {
      setAccountHeads(accounts)
      return
    }

    const allowedTypes = collectNodeTypes(selectedNode)
    const filtered = accounts.filter((account) => {
      const accountType = account.account_type || ''
      const classification = account.classification || ''
      const internalGroup = account.internal_group || ''
      return (
        allowedTypes.has(accountType) ||
        allowedTypes.has(classification) ||
        allowedTypes.has(internalGroup)
      )
    })

    setAccountHeads(filtered)
  }, [selectedNode, accounts])

  const handleNodeSelect = (node: TreeNode | null) => {
    setSelectedNode(node)
  }

  const handleFilterChange = (_codePrefix: string | null) => {
    if (!_codePrefix) {
      setSelectedNode(null)
      setAccountHeads(accounts)
    }
  }

  const refreshAccounts = () => {
    const subscription = getChartOfAccounts({
      view: 'all',
      include_inactive: true,
    }).subscribe({
      next: (res: any) => {
        if (res.response?.data) {
          setAccounts(res.response.data)
          setAccountHeads(res.response.data)
        }
      },
      error: (error) => {
        console.error('Error fetching accounts:', error)
      },
    })

    return () => subscription.unsubscribe()
  }

  const handleAddNewClose = (_response: any, action: string) => {
    setShowAddNew(false)
    if (action === 'success' || action === 'add') {
      refreshAccounts()
    }
  }

  const selectedTone = useMemo(
    () => classificationTone(selectedNode?.type || selectedNode?.name),
    [selectedNode],
  )

  const columns = useMemo(
    () => [
      {
        name: 'Code',
        dataKey: 'code',
        width: '10%',
        selector: (row: Account) => row.code || '',
      },
      {
        name: 'Name',
        dataKey: 'name',
        width: '22%',
        selector: (row: Account) => row.name || '',
      },
      {
        name: 'Classification',
        dataKey: 'classification',
        width: '12%',
        selector: (row: Account) => {
          const tone = classificationTone(
            row.classification || row.internal_group || row.account_type,
          )
          return (
            <ClassificationBadge
              $accent={tone.accent}
              $accentSoft={tone.accentSoft}
            >
              {row.classification || '--'}
            </ClassificationBadge>
          )
        },
      },
      {
        name: 'Type',
        dataKey: 'account_type',
        width: '14%',
        selector: (row: Account) => (
          <TypeText title={row.account_type || ''}>
            {row.account_type || ''}
          </TypeText>
        ),
      },
      {
        name: 'Postable',
        dataKey: 'allow_posting',
        width: '8%',
        selector: (row: Account) => (
          <StatusBadge $tone={row.allow_posting ? 'success' : 'warning'}>
            {row.allow_posting ? 'Postable' : 'Group'}
          </StatusBadge>
        ),
      },
      {
        name: 'Active',
        dataKey: 'is_active',
        width: '8%',
        selector: (row: Account) => (
          <StatusBadge $tone={row.is_active ? 'success' : 'danger'}>
            {row.is_active ? 'Active' : 'Inactive'}
          </StatusBadge>
        ),
      },
      {
        name: 'Reconcile',
        dataKey: 'reconcile',
        width: '8%',
        selector: (row: Account) => (
          <StatusBadge $tone={row.reconcile ? 'success' : 'neutral'}>
            {row.reconcile ? 'Enabled' : 'No'}
          </StatusBadge>
        ),
      },
      {
        name: 'Dimensions',
        dataKey: 'dimensions',
        width: '14%',
        selector: (row: Account) => {
          const dimensions = [
            row.default_partner_required ? 'Partner' : '',
            row.default_cost_center_required ? 'Cost Center' : '',
          ].filter(Boolean)

          if (!dimensions.length) {
            return <StatusBadge $tone="neutral">None</StatusBadge>
          }

          return (
            <DimensionWrap>
              {dimensions.map((dimension) => (
                <DimensionBadge key={dimension}>{dimension}</DimensionBadge>
              ))}
            </DimensionWrap>
          )
        },
      },
      {
        dataField: 'balance',
        width: '12%',
        name: 'Balance',
        selector: (row: Account) => {
          if (row.balance === null || row.balance === undefined) {
            return '--'
          }
          return row.balance.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })
        },
      },
    ],
    [],
  )

  return (
    <PageLayout
      isShowSpinner={isShowSpinner}
      onAddNew={() => setShowAddNew(true)}
      showAddNew={showAddNew}
      onCloseAddNew={() => setShowAddNew(false)}
      selectedNode={selectedNode}
      onAddNewClose={handleAddNewClose}
      treeNodes={treeNodes}
    >
      <div
        style={{
          display: 'flex',
          gap: '16px',
          width: '100%',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            width: '280px',
            minWidth: '280px',
            maxHeight: 'calc(100vh - 235px)',
            overflow: 'hidden',
          }}
        >
          <AccountFilters
            nodes={treeNodes}
            onNodeSelect={handleNodeSelect}
            selectedNodeId={selectedNode?.id || null}
            onFilterChange={handleFilterChange}
          />
        </div>
        <SectionContent style={{ width: '100%', minHeight: '220px' }}>
          {selectedNode ? (
            <FilterBanner
              $accent={selectedTone.accent}
              $accentSoft={selectedTone.accentSoft}
            >
              <div>
                <FilterTitle>
                  <FilterDot $accent={selectedTone.accent} />
                  <FilterText>
                    Filtering by {selectedNode.name} and its descendant accounts
                  </FilterText>
                </FilterTitle>
                <FilterHint>
                  Accounts in the table inherit this family color so the
                  selected branch stays easy to scan.
                </FilterHint>
              </div>
              <ClassificationBadge
                $accent={selectedTone.accent}
                $accentSoft={selectedTone.accentSoft}
              >
                {selectedNode.code}
              </ClassificationBadge>
            </FilterBanner>
          ) : null}

          {accountHeads.length ? (
            <PixelTable
              data={accountHeads}
              columns={columns}
              pagination
              searchable
              sortable
              fixedHeader={true}
              fixedHeaderScrollHeight="calc(100vh - 245px)"
            />
          ) : (
            <EmptyState>
              <EmptyTitle>No accounts matched this filter</EmptyTitle>
              <span>
                Try a different account family or clear the current filter.
              </span>
            </EmptyState>
          )}
        </SectionContent>
      </div>
    </PageLayout>
  )
}

export default ChartOfAccounts
